/**
 * BrandedSpinner Component (CSS-only, no framer-motion)
 * 
 * Branded loading spinner with the S| logo in the center
 * and an animated arc spinning around it.
 */

// ============================================
// Types
// ============================================

interface BrandedSpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes on the wrapper */
  className?: string;
}

// ============================================
// Constants
// ============================================

const sizeConfig = {
  sm: { container: 40, logo: 18, strokeWidth: 2.5 },
  md: { container: 64, logo: 28, strokeWidth: 3 },
  lg: { container: 80, logo: 36, strokeWidth: 3.5 },
};

// S| logo path data (from src/icons/icon.svg, viewBox 0 0 162 258)
const S_PATH =
  'M87.9883 172.086C87.9883 169.352 87.5651 166.91 86.7188 164.762C85.9375 162.548 84.4727 160.562 82.3242 158.805C80.2409 156.982 77.2786 155.224 73.4375 153.531C69.6615 151.773 64.7786 149.951 58.7891 148.062C52.1484 145.979 45.9635 143.635 40.2344 141.031C34.5052 138.427 29.4596 135.4 25.0977 131.949C20.8008 128.499 17.4479 124.527 15.0391 120.035C12.6302 115.478 11.4258 110.204 11.4258 104.215C11.4258 98.3555 12.6628 93.0169 15.1367 88.1992C17.6758 83.3815 21.2565 79.2474 25.8789 75.7969C30.5013 72.2812 35.9375 69.5794 42.1875 67.6914C48.5026 65.8034 55.4688 64.8594 63.0859 64.8594C73.6328 64.8594 82.7474 66.7799 90.4297 70.6211C98.1771 74.4622 104.167 79.6706 108.398 86.2461C112.63 92.8216 114.746 100.211 114.746 108.414H87.9883C87.9883 103.987 87.0443 100.081 85.1562 96.6953C83.3333 93.3099 80.5339 90.6406 76.7578 88.6875C73.0469 86.7344 68.3594 85.7578 62.6953 85.7578C57.2266 85.7578 52.6693 86.5716 49.0234 88.1992C45.4427 89.8268 42.7409 92.0404 40.918 94.8398C39.1602 97.6393 38.2812 100.764 38.2812 104.215C38.2812 106.819 38.8997 109.163 40.1367 111.246C41.3737 113.264 43.2292 115.185 45.7031 117.008C48.1771 118.766 51.237 120.426 54.8828 121.988C58.5938 123.486 62.8906 124.983 67.7734 126.48C75.5859 128.824 82.4219 131.428 88.2812 134.293C94.2057 137.158 99.1211 140.413 103.027 144.059C106.999 147.704 109.961 151.806 111.914 156.363C113.932 160.921 114.941 166.096 114.941 171.891C114.941 178.01 113.737 183.479 111.328 188.297C108.919 193.115 105.469 197.216 100.977 200.602C96.4844 203.987 91.0807 206.559 84.7656 208.316C78.5156 210.074 71.5169 210.953 63.7695 210.953C56.8685 210.953 50.0326 210.042 43.2617 208.219C36.556 206.331 30.4688 203.531 25 199.82C19.5312 196.044 15.1693 191.292 11.9141 185.562C8.65885 179.768 7.03125 172.997 7.03125 165.25H33.9844C33.9844 169.742 34.7005 173.583 36.1328 176.773C37.6302 179.898 39.7135 182.47 42.3828 184.488C45.1172 186.441 48.3073 187.874 51.9531 188.785C55.599 189.697 59.5378 190.152 63.7695 190.152C69.2383 190.152 73.7305 189.404 77.2461 187.906C80.8268 186.344 83.4961 184.195 85.2539 181.461C87.0768 178.727 87.9883 175.602 87.9883 172.086Z';

const PIPE_PATH = 'M144.301 42.8125V211.367H127.992V42.8125H144.301Z';

// ============================================
// Component
// ============================================

export function BrandedSpinner({
  size = 'md',
  className = '',
}: BrandedSpinnerProps) {
  const cfg = sizeConfig[size];
  const r = 29;
  const circumference = 2 * Math.PI * r;

  return (
    <>
      <style>{`
        @keyframes branded-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes branded-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        .branded-arc {
          animation: branded-spin 1.2s linear infinite;
          transform-origin: center;
        }
        .branded-logo {
          animation: branded-pulse 2s ease-in-out infinite;
        }
      `}</style>
      <div
        className={`relative inline-flex items-center justify-center ${className}`}
        style={{ width: cfg.container, height: cfg.container }}
      >
        {/* Static background ring */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 64 64"
          fill="none"
        >
          <circle
            cx="32"
            cy="32"
            r={r}
            stroke="#DBEAFE"
            strokeWidth={cfg.strokeWidth}
          />
        </svg>

        {/* Spinning arc */}
        <svg
          className="absolute inset-0 w-full h-full branded-arc"
          viewBox="0 0 64 64"
          fill="none"
        >
          <circle
            cx="32"
            cy="32"
            r={r}
            stroke="#1A73E8"
            strokeWidth={cfg.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${circumference * 0.3} ${circumference * 0.7}`}
            fill="none"
          />
        </svg>

        {/* Center S| logo */}
        <svg
          viewBox="0 0 162 258"
          fill="none"
          className="relative z-10 branded-logo"
          style={{ width: cfg.logo, height: cfg.logo * (258 / 162) }}
        >
          <path d={S_PATH} fill="#1A73E8" />
          <path d={PIPE_PATH} fill="currentColor" className="text-neutral-800 dark:text-white" />
        </svg>
      </div>
    </>
  );
}

export default BrandedSpinner;
